'use client'
import { useEffect, useRef, useState } from 'react'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Typography, TypographyLongForm } from 'components/Contentful'
import { BLOCKS } from '@contentful/rich-text-types'
import { ArticleTOC } from 'components/ArticleTOC'
import { removeSpecialCharacters } from 'utils/shared'

function KeyTakeawaysContent({ content }) {
  const { keyTakeaways, mainContent } = useMemoizedContentGetter(content, [
    'keyTakeaways',
    'mainContent',
  ])

  const [tableOfContents, setTableOfContents] = useState([])
  const [activeSection, setActiveSection] = useState()
  const articleContainerRef = useRef(null)
  const refs = useRef({})

  const mainBody = mainContent?.textContent

  // Generate table of contents from H2 elements in mainContent
  useEffect(() => {
    const articles = []
    const richTextContent = mainBody?.json?.content ?? mainBody?.content
    if (richTextContent) {
      for (let i = 0; i < richTextContent.length; i++) {
        const element = richTextContent[i]
        if (element.nodeType === BLOCKS.HEADING_2) {
          const sectionContent = element?.content?.[0]?.value ?? ''
          articles.push({
            text: sectionContent,
            id: removeSpecialCharacters(
              `${BLOCKS.HEADING_2}-${sectionContent.slice(0, 50).replaceAll(' ', '-')}`
            ),
          })
        }
      }
    }
    setTableOfContents(articles)
  }, [mainBody])

  // Track active section via IntersectionObserver
  useEffect(() => {
    if (!tableOfContents?.length) return

    setActiveSection(tableOfContents[0]?.id)

    const container = articleContainerRef.current
    tableOfContents.forEach(({ id }) => {
      refs.current[id] = container?.querySelector(`#${CSS.escape(id)}`) ?? null
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id)
        })
      },
      {
        root: null,
        rootMargin: '-30% 0px -30% 0px',
        threshold: 0,
      }
    )

    tableOfContents.forEach(({ id }) => {
      if (refs.current[id]) observer.observe(refs.current[id])
    })

    const currentRefs = refs.current

    return () => {
      tableOfContents.forEach(({ id }) => {
        if (currentRefs[id]) observer.unobserve(currentRefs[id])
      })
    }
  }, [tableOfContents])

  const showTOC = tableOfContents?.length >= 1

  return (
    <section
      className="mx-auto max-w-7xl px-5 py-12 md:px-6 md:py-16"
      ref={articleContainerRef}
      data-testid="keyTakeaways-section"
    >
      {keyTakeaways?.textContent && (
        <div className="w-full pb-8 [&_p]:pb-5" data-testid="keyTakeaways-content">
          <Typography
            content={keyTakeaways.textContent}
            className="lg:text-[14px]!"
            variantOverride={{
              [BLOCKS.HEADING_2]: 'headingMd',
              [BLOCKS.PARAGRAPH]: 'bodyMd',
            }}
          />
        </div>
      )}

      <div
        className={`flex flex-col ${showTOC ? 'lg:flex-row lg:gap-8' : ''}`}
        data-testid="expanded-content-wrapper"
      >
        {showTOC && (
          <div className="hidden lg:block lg:w-1/4" data-testid="table-of-contents-wrapper">
            <div className="sticky top-24" data-testid="table-of-contents">
              <ArticleTOC
                content={tableOfContents}
                withTitle={false}
                variantItem="labelSm"
                activeSection={activeSection}
              />
            </div>
          </div>
        )}

        {mainBody && (
          <div className={showTOC ? 'w-full lg:w-3/4' : 'w-full'} data-testid="main-content">
            <TypographyLongForm
              content={mainBody}
              variantOverride={{
                [BLOCKS.HEADING_2]: 'headingMd',
                [BLOCKS.PARAGRAPH]: 'bodyMd',
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}

export { KeyTakeawaysContent }
export default KeyTakeawaysContent
